import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Label } from './components/ui/label';
import { Switch } from './components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './components/ui/alert-dialog';
import { Home, MapPin, Bed, Bath, Square, Phone, Mail, Instagram, Facebook, MessageCircle, Settings, Plus, Edit, Trash2, Star, Menu, X, Calendar, Gift, ZoomIn, Clock, Truck, CheckCircle, Upload, Loader2 } from 'lucide-react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve image URLs - relative paths get prepended with API_BASE_URL
const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/files/')) return `${API_BASE_URL}${url}`;
  return url;
};

function App() {
  const [properties, setProperties] = useState([]);
  const [news, setNews] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState({ username: '', password: '' });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedImage, setSelectedImage] = useState(null);
  const [addPropertyOpen, setAddPropertyOpen] = useState(false);
  const [addPromotionOpen, setAddPromotionOpen] = useState(false);
  const [addNewsOpen, setAddNewsOpen] = useState(false);
  const [addStoryOpen, setAddStoryOpen] = useState(false);
  const [viewProperty, setViewProperty] = useState(null);
  const [viewPromotion, setViewPromotion] = useState(null);
  const [propertyFilter, setPropertyFilter] = useState('all');

  useEffect(() => {
    fetchProperties();
    fetchNews();
    fetchSuccessStories();
    fetchPromotions();
    fetchSettings();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties`);
      const data = await response.json();
      setProperties(data.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchNews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/news`);
      const data = await response.json();
      setNews(data.news || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };

  const fetchSuccessStories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/success-stories`);
      const data = await response.json();
      setSuccessStories(data.stories || []);
    } catch (error) {
      console.error('Error fetching success stories:', error);
    }
  };

  const fetchPromotions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promotions`);
      const data = await response.json();
      setPromotions(data.promotions || []);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings`);
      const data = await response.json();
      setSiteSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleAdminLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adminCredentials)
      });
      if (response.ok) {
        setIsAdminMode(true);
        setActiveTab('admin');
      } else {
        alert('Credenciales incorrectas');
      }
    } catch (error) {
      console.error('Error during login:', error);
    }
  };

  const handleWhatsAppClick = (property) => {
    const message = property
      ? `Hola, estoy interesado en la propiedad: ${property.title} - $${property.price.toLocaleString()}`
      : siteSettings.whatsapp_message || 'Hola, estoy interesado en sus propiedades';
    const whatsappUrl = `https://wa.me/${siteSettings.whatsapp_number?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // --- CRUD functions ---
  const saveProperty = async (propertyData) => {
    try {
      const url = propertyData.id
        ? `${API_BASE_URL}/api/properties/${propertyData.id}`
        : `${API_BASE_URL}/api/properties`;
      const method = propertyData.id ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(propertyData) });
      fetchProperties();
    } catch (error) { console.error('Error saving property:', error); }
  };

  const deleteProperty = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/properties/${id}`, { method: 'DELETE' });
      fetchProperties();
    } catch (error) { console.error('Error deleting property:', error); }
  };

  const saveNews = async (newsData) => {
    try {
      const url = newsData.id
        ? `${API_BASE_URL}/api/news/${newsData.id}`
        : `${API_BASE_URL}/api/news`;
      const method = newsData.id ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newsData) });
      fetchNews();
    } catch (error) { console.error('Error saving news:', error); }
  };

  const deleteNews = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/news/${id}`, { method: 'DELETE' });
      fetchNews();
    } catch (error) { console.error('Error deleting news:', error); }
  };

  const savePromotion = async (promotionData) => {
    try {
      const url = promotionData.id
        ? `${API_BASE_URL}/api/promotions/${promotionData.id}`
        : `${API_BASE_URL}/api/promotions`;
      const method = promotionData.id ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(promotionData) });
      fetchPromotions();
    } catch (error) { console.error('Error saving promotion:', error); }
  };

  const deletePromotion = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/promotions/${id}`, { method: 'DELETE' });
      fetchPromotions();
    } catch (error) { console.error('Error deleting promotion:', error); }
  };

  const saveSuccessStory = async (storyData) => {
    try {
      const url = storyData.id
        ? `${API_BASE_URL}/api/success-stories/${storyData.id}`
        : `${API_BASE_URL}/api/success-stories`;
      const method = storyData.id ? 'PUT' : 'POST';
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(storyData) });
      fetchSuccessStories();
    } catch (error) { console.error('Error saving success story:', error); }
  };

  const deleteSuccessStory = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/success-stories/${id}`, { method: 'DELETE' });
      fetchSuccessStories();
    } catch (error) { console.error('Error deleting success story:', error); }
  };

  const saveSettings = async (settingsData) => {
    try {
      await fetch(`${API_BASE_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsData)
      });
      fetchSettings();
    } catch (error) { console.error('Error saving settings:', error); }
  };

  // ========================
  // CARD COMPONENTS (Public)
  // ========================

  const PropertyCard = ({ property, isAdmin = false }) => (
    <Card data-testid={`property-card-${property.id}`} className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 bg-white/90 backdrop-blur-sm border-0 overflow-hidden">
      <div className="relative">
        <img
          src={resolveImageUrl(property.image_url)}
          alt={property.title}
          className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
          onClick={() => setSelectedImage(resolveImageUrl(property.image_url))}
        />
        <div className="absolute top-2 right-2 bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
             onClick={() => setSelectedImage(resolveImageUrl(property.image_url))}>
          <ZoomIn className="w-4 h-4" />
        </div>
        {property.is_featured && (
          <Badge className="absolute top-4 left-4 bg-amber-500 hover:bg-amber-600 mr-2">Destacado</Badge>
        )}
        <Badge className={`absolute ${property.is_featured ? 'top-12' : 'top-4'} left-4 ${property.status === 'available' ? 'bg-green-500' : property.status === 'sold' ? 'bg-red-500' : 'bg-blue-500'}`}>
          {property.status === 'available' ? 'Disponible' : property.status === 'sold' ? 'Vendido' : 'Reservado'}
        </Badge>
        <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-lg font-bold">
          ${property.price?.toLocaleString()}
        </div>
      </div>
      <CardContent className="p-6">
        <h3 className="text-xl font-bold mb-2 text-gray-800 cursor-pointer hover:text-amber-600 transition-colors" onClick={() => !isAdmin && setViewProperty(property)}>{property.title}</h3>
        {property.project_name && (
          <p className="text-amber-600 font-medium mb-2">Proyecto: {property.project_name}</p>
        )}
        <p className="text-gray-600 mb-2 line-clamp-2">{property.description}</p>
        {!isAdmin && (
          <button onClick={() => setViewProperty(property)} className="text-amber-600 text-sm font-medium mb-4 hover:underline">Ver mas detalles</button>
        )}
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            <span>{property.location}</span>
          </div>
          <div className="flex items-center gap-1">
            <Square className="w-4 h-4" />
            <span>{property.area}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-4 text-sm">
          {property.delivery_type === 'immediate' ? (
            <div className="flex items-center gap-1 text-green-600">
              <Truck className="w-4 h-4" />
              <span className="font-medium">Entrega Inmediata</span>
            </div>
          ) : property.delivery_date ? (
            <div className="flex items-center gap-1 text-blue-600">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Entrega: {property.delivery_date}</span>
            </div>
          ) : null}
        </div>
        {(property.bedrooms || property.bathrooms) && (
          <div className="flex items-center gap-4 mb-4 text-sm">
            {property.bedrooms && (
              <div className="flex items-center gap-1"><Bed className="w-4 h-4" /><span>{property.bedrooms} hab.</span></div>
            )}
            {property.bathrooms && (
              <div className="flex items-center gap-1"><Bath className="w-4 h-4" /><span>{property.bathrooms} banos</span></div>
            )}
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          {isAdmin ? (
            <>
              <Button data-testid={`edit-property-${property.id}`} variant="outline" size="sm" onClick={() => setSelectedProperty(property)}>
                <Edit className="w-4 h-4 mr-1" /> Editar
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid={`delete-property-${property.id}`} variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminar propiedad?</AlertDialogTitle>
                    <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteProperty(property.id)}>Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <Button
              data-testid={`whatsapp-property-${property.id}`}
              onClick={() => handleWhatsAppClick(property)}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Consultar por WhatsApp
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const PromotionCard = ({ promotion }) => (
    <Card data-testid={`promotion-card-${promotion.id}`} className="group overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 bg-gradient-to-br from-amber-50 to-orange-50">
      {promotion.image_url && (
        <div className="relative">
          <img
            src={resolveImageUrl(promotion.image_url)}
            alt={promotion.title}
            className="w-full h-52 object-cover cursor-pointer"
            onClick={() => setSelectedImage(resolveImageUrl(promotion.image_url))}
          />
          {promotion.is_featured && (
            <Badge className="absolute top-4 left-4 bg-amber-500 hover:bg-amber-600">Destacado</Badge>
          )}
        </div>
      )}
      <CardContent className="p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-1 cursor-pointer hover:text-amber-600 transition-colors" onClick={() => setViewPromotion(promotion)}>{promotion.title}</h3>
        <p className="text-amber-600 font-medium text-sm mb-3">Proyecto: {promotion.project_name}</p>
        <p className="text-gray-600 mb-2 line-clamp-2">{promotion.description}</p>
        <button onClick={() => setViewPromotion(promotion)} className="text-amber-600 text-sm font-medium mb-4 hover:underline">Ver mas detalles</button>
        {promotion.benefits && promotion.benefits.length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">Beneficios incluidos:</p>
            <ul className="space-y-1">
              {promotion.benefits.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>Valido hasta: {promotion.valid_until}</span>
          </div>
        </div>
        {promotion.terms_conditions && (
          <p className="text-xs text-gray-400 italic mb-4">*{promotion.terms_conditions}</p>
        )}
        <Button
          onClick={() => handleWhatsAppClick()}
          className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
        >
          <MessageCircle className="w-4 h-4 mr-2" /> Consultar Promocion
        </Button>
      </CardContent>
    </Card>
  );

  const NewsCard = ({ article }) => (
    <Card data-testid={`news-card-${article.id}`} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
      {article.image_url && (
        <img
          src={resolveImageUrl(article.image_url)}
          alt={article.title}
          className="w-full h-48 object-cover cursor-pointer"
          onClick={() => setSelectedImage(resolveImageUrl(article.image_url))}
        />
      )}
      <CardContent className="p-6">
        {article.is_featured && <Badge className="bg-amber-500 hover:bg-amber-600 mb-2">Destacado</Badge>}
        <h3 className="text-lg font-semibold mb-2">{article.title}</h3>
        <p className="text-gray-600 text-sm mb-3">{new Date(article.event_date).toLocaleDateString()}</p>
        <p className="text-gray-700 line-clamp-3">{article.content}</p>
      </CardContent>
    </Card>
  );

  const SuccessStoryCard = ({ story }) => (
    <Card data-testid={`story-card-${story.id}`} className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
      <CardContent className="p-6">
        <div className="flex items-center mb-4">
          {[...Array(story.rating)].map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-current text-yellow-400" />
          ))}
        </div>
        <p className="text-gray-700 mb-4 italic">"{story.story}"</p>
        <div className="border-t pt-4">
          <p className="font-semibold text-gray-800">{story.client_name}</p>
          <p className="text-sm text-gray-600">Cliente satisfecho - {story.property_title}</p>
        </div>
      </CardContent>
    </Card>
  );

  // ========================
  // FORM COMPONENTS (Admin)
  // ========================

  const PropertyForm = ({ property, onSave, onClose }) => {
    const existingImages = property ? [property.image_url, ...(property.gallery_images || [])].filter(Boolean) : [];
    const [fd, setFd] = useState(property || {
      title: '', description: 'Hermosa propiedad con excelente ubicacion, acabados de primera calidad y amplios espacios. Ideal para familias que buscan comodidad y seguridad.', price: '', area: '', location: '', property_type: '',
      bedrooms: '', bathrooms: '', image_url: '', gallery_images: [], features: [], status: 'available',
      delivery_type: 'immediate', delivery_date: '', project_name: '', is_featured: false
    });
    const [imageList, setImageList] = useState(existingImages);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleFileSelect = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      setUploading(true); setUploadError('');
      for (const file of files.slice(0, 5 - imageList.length)) {
        try {
          const formData = new FormData(); formData.append('file', file);
          const resp = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
          if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Error'); }
          const data = await resp.json();
          setImageList(prev => [...prev, `${data.url}`]);
        } catch (err) { setUploadError(`Error: ${err.message}`); }
      }
      setUploading(false); e.target.value = '';
    };

    const removeImage = (index) => setImageList(imageList.filter((_, i) => i !== index));

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (imageList.length === 0) { alert('Debe agregar al menos una imagen'); return; }
      setSaving(true);
      const success = await onSave({
        ...fd, image_url: imageList[0], gallery_images: imageList.slice(1),
        price: parseFloat(fd.price) || 0, bedrooms: parseInt(fd.bedrooms) || null,
        bathrooms: parseInt(fd.bathrooms) || null, is_featured: !!fd.is_featured,
      });
      setSaving(false);
      if (success !== false) onClose();
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="property-form">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Titulo</Label><Input data-testid="property-title-input" value={fd.title} onChange={(e) => setFd({...fd, title: e.target.value})} required /></div>
          <div><Label>Precio</Label><Input data-testid="property-price-input" type="number" value={fd.price} onChange={(e) => setFd({...fd, price: e.target.value})} required /></div>
        </div>
        <div><Label>Descripcion</Label><Textarea data-testid="property-description-input" value={fd.description} onChange={(e) => setFd({...fd, description: e.target.value})} required /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Ubicacion</Label><Input data-testid="property-location-input" value={fd.location} onChange={(e) => setFd({...fd, location: e.target.value})} required /></div>
          <div><Label>Area</Label><Input data-testid="property-area-input" value={fd.area} onChange={(e) => setFd({...fd, area: e.target.value})} required /></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Tipo</Label>
            <Select value={fd.property_type} onValueChange={(v) => setFd({...fd, property_type: v})}>
              <SelectTrigger data-testid="property-type-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="casa">Casa</SelectItem>
                <SelectItem value="apartamento">Apartamento</SelectItem>
                <SelectItem value="terreno">Terreno</SelectItem>
                <SelectItem value="local">Local Comercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Habitaciones</Label><Input type="number" value={fd.bedrooms} onChange={(e) => setFd({...fd, bedrooms: e.target.value})} /></div>
          <div><Label>Banos</Label><Input type="number" value={fd.bathrooms} onChange={(e) => setFd({...fd, bathrooms: e.target.value})} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Nombre del Proyecto</Label><Input data-testid="property-project-input" value={fd.project_name || ''} onChange={(e) => setFd({...fd, project_name: e.target.value})} placeholder="Ej: Asturia" /></div>
          <div>
            <Label>Tipo de Entrega</Label>
            <Select value={fd.delivery_type || 'immediate'} onValueChange={(v) => setFd({...fd, delivery_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="immediate">Inmediata</SelectItem><SelectItem value="future">Futura</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        {fd.delivery_type === 'future' && (
          <div><Label>Fecha de Entrega</Label><Input type="date" value={fd.delivery_date || ''} onChange={(e) => setFd({...fd, delivery_date: e.target.value})} /></div>
        )}

        {/* Image Uploader */}
        <div className="space-y-3">
          <Label>Imagenes de la Propiedad ({imageList.length}/5)</Label>
          {imageList.length > 0 && (
            <div className="grid grid-cols-5 gap-2">
              {imageList.map((url, i) => (
                <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border">
                  <img src={resolveImageUrl(url)} alt={`Imagen ${i+1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          {imageList.length < 5 && (
            <label data-testid="property-upload-area" className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploading ? 'border-amber-400 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/50'}`}>
              {uploading ? (<div className="flex items-center gap-2 text-amber-600"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm font-medium">Subiendo...</span></div>)
              : (<><Upload className="w-6 h-6 text-gray-400 mb-1" /><span className="text-sm text-gray-500">Seleccionar archivos</span><span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP (max 10MB)</span></>)}
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple onChange={handleFileSelect} className="hidden" disabled={uploading} />
            </label>
          )}
          {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
        </div>

        <div>
          <Label>Estado</Label>
          <Select value={fd.status} onValueChange={(v) => setFd({...fd, status: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="available">Disponible</SelectItem><SelectItem value="sold">Vendido</SelectItem><SelectItem value="reserved">Reservado</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
          <Switch data-testid="property-featured-toggle" checked={!!fd.is_featured} onCheckedChange={(v) => setFd({...fd, is_featured: v})} />
          <Label className="font-medium">Propiedad Destacada</Label>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="property-form-submit" type="submit" disabled={saving}>{saving ? 'Guardando...' : (property?.id ? 'Actualizar' : 'Crear')} {saving ? '' : 'Propiedad'}</Button>
        </div>
      </form>
    );
  };

  const NewsForm = ({ article, onSave, onClose }) => {
    const [fd, setFd] = useState(article || {
      title: '', content: 'Nos complace anunciar un nuevo evento en nuestra comunidad. Acompanenos a descubrir las ultimas novedades en bienes raices y las oportunidades de inversion que tenemos para usted.', image_url: '', event_date: '', is_featured: false
    });
    const [imageList, setImageList] = useState(article?.image_url ? [article.image_url] : []);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleFileSelect = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length || imageList.length >= 1) return;
      setUploading(true); setUploadError('');
      try {
        const formData = new FormData(); formData.append('file', files[0]);
        const resp = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
        if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Error'); }
        const data = await resp.json();
        setImageList([`${data.url}`]);
      } catch (err) { setUploadError(`Error: ${err.message}`); }
      setUploading(false); e.target.value = '';
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      const success = await onSave({ ...fd, image_url: imageList[0] || '', is_featured: !!fd.is_featured });
      setSaving(false);
      if (success !== false) onClose();
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="news-form">
        <div><Label>Titulo</Label><Input data-testid="news-title-input" value={fd.title} onChange={(e) => setFd({...fd, title: e.target.value})} required /></div>
        <div><Label>Contenido</Label><Textarea data-testid="news-content-input" value={fd.content} onChange={(e) => setFd({...fd, content: e.target.value})} rows={5} required /></div>

        {/* Image Uploader */}
        <div className="space-y-3">
          <Label>Imagen de la Noticia ({imageList.length}/1)</Label>
          {imageList.length > 0 && (
            <div className="relative group w-32 aspect-square rounded-lg overflow-hidden border">
              <img src={resolveImageUrl(imageList[0])} alt="Imagen" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImageList([])} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </div>
          )}
          {imageList.length < 1 && (
            <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploading ? 'border-amber-400 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/50'}`}>
              {uploading ? (<div className="flex items-center gap-2 text-amber-600"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Subiendo...</span></div>)
              : (<><Upload className="w-6 h-6 text-gray-400 mb-1" /><span className="text-sm text-gray-500">Seleccionar archivo</span><span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP (max 10MB)</span></>)}
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} className="hidden" disabled={uploading} />
            </label>
          )}
          {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
        </div>

        <div><Label>Fecha del Evento</Label><Input data-testid="news-date-input" type="date" value={fd.event_date} onChange={(e) => setFd({...fd, event_date: e.target.value})} required /></div>
        <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
          <Switch data-testid="news-featured-toggle" checked={!!fd.is_featured} onCheckedChange={(v) => setFd({...fd, is_featured: v})} />
          <Label className="font-medium">Noticia Destacada</Label>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="news-form-submit" type="submit" disabled={saving}>{saving ? 'Guardando...' : (article?.id ? 'Actualizar' : 'Crear')} {saving ? '' : 'Noticia'}</Button>
        </div>
      </form>
    );
  };

  const PromotionForm = ({ promotion, onSave, onClose }) => {
    const [fd, setFd] = useState(promotion || {
      title: '', description: 'Aprovecha esta promocion exclusiva con beneficios unicos para tu nuevo hogar. Incluye acabados de primera, electrodomesticos y mucho mas. Consulta condiciones.', project_name: '', valid_until: '', image_url: '',
      benefits: [], terms_conditions: '', is_active: true, is_featured: false
    });
    const [imageList, setImageList] = useState(promotion?.image_url ? [promotion.image_url] : []);
    const [benefitInput, setBenefitInput] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleFileSelect = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      setUploading(true);
      setUploadError('');
      for (const file of files.slice(0, 5 - imageList.length)) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const resp = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
          if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Error'); }
          const data = await resp.json();
          setImageList(prev => [...prev, `${data.url}`]);
        } catch (err) { setUploadError(`Error: ${err.message}`); }
      }
      setUploading(false);
      e.target.value = '';
    };

    const removeImage = (index) => setImageList(imageList.filter((_, i) => i !== index));

    const addBenefit = () => {
      if (benefitInput.trim()) {
        setFd({...fd, benefits: [...(fd.benefits || []), benefitInput.trim()]});
        setBenefitInput('');
      }
    };

    const removeBenefit = (index) => {
      setFd({...fd, benefits: fd.benefits.filter((_, i) => i !== index)});
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      const success = await onSave({ ...fd, image_url: imageList[0] || '', is_featured: !!fd.is_featured, is_active: !!fd.is_active });
      setSaving(false);
      if (success !== false) onClose();
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="promotion-form">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Titulo</Label>
            <Input data-testid="promotion-title-input" value={fd.title} onChange={(e) => setFd({...fd, title: e.target.value})} required />
          </div>
          <div>
            <Label>Nombre del Proyecto</Label>
            <Input data-testid="promotion-project-input" value={fd.project_name} onChange={(e) => setFd({...fd, project_name: e.target.value})} required />
          </div>
        </div>
        <div>
          <Label>Descripcion</Label>
          <Textarea data-testid="promotion-description-input" value={fd.description} onChange={(e) => setFd({...fd, description: e.target.value})} required />
        </div>

        {/* Image Uploader */}
        <div className="space-y-3">
          <Label>Imagenes de la Promocion ({imageList.length}/5)</Label>
          {imageList.length > 0 && (
            <div className="grid grid-cols-5 gap-2">
              {imageList.map((url, i) => (
                <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border">
                  <img src={resolveImageUrl(url)} alt={`Imagen ${i+1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {imageList.length < 5 && (
            <label data-testid="promotion-upload-area" className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploading ? 'border-amber-400 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/50'}`}>
              {uploading ? (
                <div className="flex items-center gap-2 text-amber-600"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm font-medium">Subiendo...</span></div>
              ) : (
                <><Upload className="w-6 h-6 text-gray-400 mb-1" /><span className="text-sm text-gray-500">Seleccionar archivos</span><span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP (max 10MB)</span></>
              )}
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple onChange={handleFileSelect} className="hidden" disabled={uploading} />
            </label>
          )}
          {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
        </div>

        <div>
          <Label>Valido Hasta</Label>
          <Input data-testid="promotion-date-input" type="date" value={fd.valid_until} onChange={(e) => setFd({...fd, valid_until: e.target.value})} required />
        </div>
        <div>
          <Label>Beneficios</Label>
          <div className="flex gap-2 mb-2">
            <Input
              data-testid="promotion-benefit-input"
              value={benefitInput}
              onChange={(e) => setBenefitInput(e.target.value)}
              placeholder="Ej: Linea blanca completa"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBenefit(); } }}
            />
            <Button type="button" variant="outline" onClick={addBenefit} data-testid="add-benefit-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(fd.benefits || []).map((b, i) => (
              <Badge key={i} variant="secondary" className="flex items-center gap-1 py-1 px-3">
                {b}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeBenefit(i)} />
              </Badge>
            ))}
          </div>
        </div>
        <div>
          <Label>Terminos y Condiciones</Label>
          <Textarea value={fd.terms_conditions} onChange={(e) => setFd({...fd, terms_conditions: e.target.value})} />
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <Switch checked={!!fd.is_active} onCheckedChange={(v) => setFd({...fd, is_active: v})} />
            <Label className="font-medium">Activa</Label>
          </div>
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
            <Switch data-testid="promotion-featured-toggle" checked={!!fd.is_featured} onCheckedChange={(v) => setFd({...fd, is_featured: v})} />
            <Label className="font-medium">Destacada</Label>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="promotion-form-submit" type="submit" disabled={saving}>{saving ? 'Guardando...' : (promotion?.id ? 'Actualizar' : 'Crear')} {saving ? '' : 'Promocion'}</Button>
        </div>
      </form>
    );
  };

  const SuccessStoryForm = ({ story, onSave, onClose }) => {
    const [fd, setFd] = useState(story || {
      client_name: '', story: 'Gracias a Barraza360 encontramos la casa de nuestros suenos. El equipo nos acompano en cada paso del proceso con profesionalismo y dedicacion. Altamente recomendados.', property_title: '', image_url: '', rating: 5
    });
    const [imageList, setImageList] = useState(story?.image_url ? [story.image_url] : []);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleFileSelect = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length || imageList.length >= 1) return;
      setUploading(true); setUploadError('');
      try {
        const formData = new FormData(); formData.append('file', files[0]);
        const resp = await fetch(`${API_BASE_URL}/api/upload`, { method: 'POST', body: formData });
        if (!resp.ok) { const err = await resp.json(); throw new Error(err.detail || 'Error'); }
        const data = await resp.json();
        setImageList([`${data.url}`]);
      } catch (err) { setUploadError(`Error: ${err.message}`); }
      setUploading(false); e.target.value = '';
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      const success = await onSave({ ...fd, image_url: imageList[0] || '', rating: parseInt(fd.rating) || 5 });
      setSaving(false);
      if (success !== false) onClose();
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="story-form">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Nombre del Cliente</Label><Input data-testid="story-client-input" value={fd.client_name} onChange={(e) => setFd({...fd, client_name: e.target.value})} required /></div>
          <div><Label>Propiedad Adquirida</Label><Input data-testid="story-property-input" value={fd.property_title} onChange={(e) => setFd({...fd, property_title: e.target.value})} required /></div>
        </div>
        <div><Label>Historia / Testimonio</Label><Textarea data-testid="story-content-input" value={fd.story} onChange={(e) => setFd({...fd, story: e.target.value})} rows={4} required /></div>

        {/* Image Uploader */}
        <div className="space-y-3">
          <Label>Imagen del Cliente (opcional) ({imageList.length}/1)</Label>
          {imageList.length > 0 && (
            <div className="relative group w-32 aspect-square rounded-lg overflow-hidden border">
              <img src={resolveImageUrl(imageList[0])} alt="Imagen" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setImageList([])} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
            </div>
          )}
          {imageList.length < 1 && (
            <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-4 cursor-pointer transition-colors ${uploading ? 'border-amber-400 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/50'}`}>
              {uploading ? (<div className="flex items-center gap-2 text-amber-600"><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">Subiendo...</span></div>)
              : (<><Upload className="w-6 h-6 text-gray-400 mb-1" /><span className="text-sm text-gray-500">Seleccionar archivo</span><span className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WEBP (max 10MB)</span></>)}
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileSelect} className="hidden" disabled={uploading} />
            </label>
          )}
          {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
        </div>

        <div>
          <Label>Calificacion (1-5)</Label>
          <Select value={String(fd.rating || 5)} onValueChange={(v) => setFd({...fd, rating: v})}>
            <SelectTrigger data-testid="story-rating-select"><SelectValue /></SelectTrigger>
            <SelectContent>{[1,2,3,4,5].map(n => (<SelectItem key={n} value={String(n)}>{n} estrella{n > 1 ? 's' : ''}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="story-form-submit" type="submit" disabled={saving}>{saving ? 'Guardando...' : (story?.id ? 'Actualizar' : 'Crear')} {saving ? '' : 'Historia'}</Button>
        </div>
      </form>
    );
  };

  // ========================
  // ADMIN LIST ITEM CARDS
  // ========================

  const AdminNewsItem = ({ article }) => (
    <Card data-testid={`admin-news-${article.id}`} className="flex flex-col sm:flex-row overflow-hidden">
      {article.image_url && (
        <img src={resolveImageUrl(article.image_url)} alt={article.title} className="w-full sm:w-40 h-32 object-cover" />
      )}
      <CardContent className="p-4 flex-1 flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold">{article.title}</h4>
            {article.is_featured && <Badge className="bg-amber-500 text-xs">Destacado</Badge>}
          </div>
          <p className="text-sm text-gray-500">{new Date(article.event_date).toLocaleDateString()}</p>
          <p className="text-sm text-gray-600 line-clamp-2 mt-1">{article.content}</p>
        </div>
        <div className="flex gap-2 ml-4 flex-shrink-0">
          <Button data-testid={`edit-news-${article.id}`} variant="outline" size="sm" onClick={() => setSelectedNews(article)}>
            <Edit className="w-4 h-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`delete-news-${article.id}`} variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar noticia?</AlertDialogTitle>
                <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteNews(article.id)}>Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );

  const AdminPromotionItem = ({ promotion }) => (
    <Card data-testid={`admin-promotion-${promotion.id}`} className="flex flex-col sm:flex-row overflow-hidden">
      {promotion.image_url && (
        <img src={resolveImageUrl(promotion.image_url)} alt={promotion.title} className="w-full sm:w-40 h-32 object-cover" />
      )}
      <CardContent className="p-4 flex-1 flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold">{promotion.title}</h4>
            {promotion.is_featured && <Badge className="bg-amber-500 text-xs">Destacado</Badge>}
            {promotion.is_active ? (
              <Badge className="bg-green-500 text-xs">Activa</Badge>
            ) : (
              <Badge variant="secondary" className="text-xs">Inactiva</Badge>
            )}
          </div>
          <p className="text-sm text-amber-600">Proyecto: {promotion.project_name}</p>
          <p className="text-sm text-gray-600 line-clamp-2 mt-1">{promotion.description}</p>
        </div>
        <div className="flex gap-2 ml-4 flex-shrink-0">
          <Button data-testid={`edit-promotion-${promotion.id}`} variant="outline" size="sm" onClick={() => setSelectedPromotion(promotion)}>
            <Edit className="w-4 h-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`delete-promotion-${promotion.id}`} variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar promocion?</AlertDialogTitle>
                <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => deletePromotion(promotion.id)}>Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );

  const AdminStoryItem = ({ story }) => (
    <Card data-testid={`admin-story-${story.id}`} className="overflow-hidden">
      <CardContent className="p-4 flex justify-between items-start">
        <div>
          <h4 className="font-semibold">{story.client_name}</h4>
          <p className="text-sm text-amber-600">Propiedad: {story.property_title}</p>
          <div className="flex my-1">
            {[...Array(story.rating || 0)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-current text-yellow-400" />
            ))}
          </div>
          <p className="text-sm text-gray-600 line-clamp-2">{story.story}</p>
        </div>
        <div className="flex gap-2 ml-4 flex-shrink-0">
          <Button data-testid={`edit-story-${story.id}`} variant="outline" size="sm" onClick={() => setSelectedStory(story)}>
            <Edit className="w-4 h-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`delete-story-${story.id}`} variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar historia?</AlertDialogTitle>
                <AlertDialogDescription>Esta accion no se puede deshacer.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteSuccessStory(story.id)}>Eliminar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );

  // ========================
  // MAIN RENDER
  // ========================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">

      {/* Image Modal */}
      {selectedImage && (
        <div
          data-testid="image-modal"
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setSelectedImage(null)}
        >
          <img src={resolveImageUrl(selectedImage)} alt="Ampliada" className="max-w-full max-h-full object-contain rounded-lg" />
          <button className="absolute top-6 right-6 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition" onClick={() => setSelectedImage(null)}>
            <X className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Fixed WhatsApp Button */}
      {siteSettings.whatsapp_number && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            data-testid="whatsapp-float-btn"
            onClick={() => handleWhatsAppClick()}
            className="rounded-full w-14 h-14 bg-green-500 hover:bg-green-600 shadow-lg hover:shadow-xl transition-all duration-300 p-0"
          >
            <MessageCircle className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* Fixed Social Media Sidebar */}
      {(siteSettings.facebook_url || siteSettings.instagram_url || siteSettings.tiktok_url || siteSettings.whatsapp_url) && (
        <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3" data-testid="social-sidebar">
          {siteSettings.facebook_url && (
            <a href={siteSettings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
              <Facebook className="w-5 h-5" />
            </a>
          )}
          {siteSettings.instagram_url && (
            <a href={siteSettings.instagram_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
              <Instagram className="w-5 h-5" />
            </a>
          )}
          {siteSettings.tiktok_url && (
            <a href={siteSettings.tiktok_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black hover:bg-gray-800 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
            </a>
          )}
          {siteSettings.whatsapp_url && (
            <a href={siteSettings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
              <MessageCircle className="w-5 h-5" />
            </a>
          )}
        </div>
      )}

      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => setActiveTab('home')}>
              <Home className="w-8 h-8 text-amber-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">{siteSettings.company_name || 'Barraza360'}</h1>
            </div>

            <nav className="hidden md:flex items-center space-x-6">
              {[
                { key: 'home', label: 'Inicio' },
                { key: 'properties', label: 'Propiedades' },
                { key: 'promotions', label: 'Promociones' },
                { key: 'success', label: 'Casos de Exito' },
                { key: 'news', label: 'Noticias' },
              ].map(tab => (
                <button
                  key={tab.key}
                  data-testid={`nav-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`text-gray-700 hover:text-amber-600 font-medium transition-colors ${activeTab === tab.key ? 'text-amber-600 border-b-2 border-amber-600 pb-1' : ''}`}
                >
                  {tab.label}
                </button>
              ))}

              {!isAdminMode ? (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="admin-login-btn">
                      <Settings className="w-4 h-4 mr-1" /> Admin
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Acceso Administrativo</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <Input data-testid="admin-username" placeholder="Usuario" value={adminCredentials.username} onChange={(e) => setAdminCredentials({...adminCredentials, username: e.target.value})} />
                      <Input data-testid="admin-password" type="password" placeholder="Contrasena" value={adminCredentials.password} onChange={(e) => setAdminCredentials({...adminCredentials, password: e.target.value})} />
                      <Button data-testid="admin-login-submit" onClick={handleAdminLogin} className="w-full">Iniciar Sesion</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Button
                  data-testid="nav-admin"
                  onClick={() => setActiveTab('admin')}
                  variant="outline"
                  size="sm"
                  className={activeTab === 'admin' ? 'bg-amber-100' : ''}
                >
                  <Settings className="w-4 h-4 mr-1" /> Panel Admin
                </Button>
              )}
            </nav>

            <div className="md:hidden">
              <Button variant="ghost" size="sm" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {['home','properties','promotions','success','news'].map(key => (
                <button key={key} onClick={() => {setActiveTab(key); setIsMobileMenuOpen(false);}} className="block px-3 py-2 text-gray-700 hover:text-amber-600 capitalize">
                  {key === 'home' ? 'Inicio' : key === 'properties' ? 'Propiedades' : key === 'promotions' ? 'Promociones' : key === 'success' ? 'Casos de Exito' : 'Noticias'}
                </button>
              ))}
              {isAdminMode && (
                <button onClick={() => {setActiveTab('admin'); setIsMobileMenuOpen(false);}} className="block px-3 py-2 text-amber-600 font-medium">
                  Panel Admin
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>

        {/* ===== HOME ===== */}
        {activeTab === 'home' && (
          <>
            {/* Hero */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src={siteSettings.hero_image || "https://images.unsplash.com/photo-1494526585095-c41746248156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxyZWFsJTIwZXN0YXRlfGVufDB8fHx8MTc1NDMzNTYwM3ww&ixlib=rb-4.1.0&q=85"}
                  alt="Hero"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50"></div>
              </div>
              <div className="relative z-10 text-center text-white px-4">
                <h1 className="text-5xl md:text-7xl font-bold mb-6">
                  {siteSettings.company_name || 'Barraza360'}
                </h1>
                <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
                  {siteSettings.company_description || 'Empresa lider en bienes raices con anos de experiencia'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg font-semibold" onClick={() => { setActiveTab('properties'); setPropertyFilter('all'); }}>
                    Ver Propiedades
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-semibold" onClick={() => handleWhatsAppClick()}>
                    Contactar Ahora
                  </Button>
                </div>
              </div>
            </section>

            {/* Featured Properties */}
            <section className="py-20 px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">Propiedades Destacadas</h2>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto">Descubre las mejores oportunidades de inversion en bienes raices</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {(properties.filter(p => p.is_featured).length > 0
                    ? properties.filter(p => p.is_featured)
                    : properties
                  ).slice(0, 5).map(property => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
                  <Button size="lg" onClick={() => { setActiveTab('properties'); setPropertyFilter('featured'); }} className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4">
                    Ver Propiedades Destacadas
                  </Button>
                  <Button size="lg" onClick={() => { setActiveTab('properties'); setPropertyFilter('all'); }} variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-4">
                    Ver Todas las Propiedades
                  </Button>
                </div>
              </div>
            </section>

            {/* Promotions Preview on Home */}
            {promotions.length > 0 && (
              <section className="py-20 bg-gradient-to-r from-amber-50 to-orange-50 px-4">
                <div className="max-w-7xl mx-auto">
                  <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">Promociones Especiales</h2>
                    <p className="text-lg text-gray-600">Aprovecha nuestras ofertas exclusivas</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {(promotions.filter(p => p.is_featured && p.is_active !== false).length > 0
                      ? promotions.filter(p => p.is_featured && p.is_active !== false)
                      : promotions.filter(p => p.is_active !== false)
                    ).slice(0, 3).map(promo => (
                      <PromotionCard key={promo.id} promotion={promo} />
                    ))}
                  </div>
                  <div className="text-center mt-12">
                    <Button size="lg" onClick={() => setActiveTab('promotions')} className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4">
                      Ver Todas las Promociones
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {/* Success Stories Preview */}
            {successStories.length > 0 && (
              <section className="py-20 bg-gray-50 px-4">
                <div className="max-w-7xl mx-auto">
                  <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">Casos de Exito</h2>
                    <p className="text-lg text-gray-600">Lo que dicen nuestros clientes</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {successStories.slice(0, 3).map(story => (
                      <SuccessStoryCard key={story.id} story={story} />
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* ===== PROPERTIES ===== */}
        {activeTab === 'properties' && (
          <section className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  {propertyFilter === 'featured' ? 'Propiedades Destacadas' : 'Todas las Propiedades'}
                </h2>
                <p className="text-lg text-gray-600">Encuentra tu hogar ideal</p>
                <div className="flex justify-center gap-3 mt-6">
                  <Button
                    data-testid="filter-all"
                    variant={propertyFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setPropertyFilter('all')}
                  >
                    Todas
                  </Button>
                  <Button
                    data-testid="filter-featured"
                    variant={propertyFilter === 'featured' ? 'default' : 'outline'}
                    onClick={() => setPropertyFilter('featured')}
                  >
                    Destacadas
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {(propertyFilter === 'featured'
                  ? properties.filter(p => p.is_featured)
                  : properties
                ).map(property => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
              {(propertyFilter === 'featured' ? properties.filter(p => p.is_featured) : properties).length === 0 && (
                <p className="text-center text-gray-500 mt-8">
                  {propertyFilter === 'featured' ? 'No hay propiedades destacadas por el momento.' : 'No hay propiedades disponibles por el momento.'}
                </p>
              )}
            </div>
          </section>
        )}

        {/* ===== PROMOTIONS (Public) ===== */}
        {activeTab === 'promotions' && (
          <section data-testid="promotions-section" className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Promociones Especiales</h2>
                <p className="text-lg text-gray-600">Ofertas y beneficios exclusivos para ti</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {promotions.filter(p => p.is_active !== false).map(promo => (
                  <PromotionCard key={promo.id} promotion={promo} />
                ))}
              </div>
              {promotions.filter(p => p.is_active !== false).length === 0 && (
                <p className="text-center text-gray-500 mt-8">No hay promociones activas por el momento.</p>
              )}
            </div>
          </section>
        )}

        {/* ===== SUCCESS STORIES ===== */}
        {activeTab === 'success' && (
          <section className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Casos de Exito</h2>
                <p className="text-lg text-gray-600">Historias de clientes satisfechos</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {successStories.map(story => (
                  <SuccessStoryCard key={story.id} story={story} />
                ))}
              </div>
              {successStories.length === 0 && (
                <p className="text-center text-gray-500 mt-8">No hay historias de exito por el momento.</p>
              )}
            </div>
          </section>
        )}

        {/* ===== NEWS ===== */}
        {activeTab === 'news' && (
          <section className="py-20 px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Noticias y Eventos</h2>
                <p className="text-lg text-gray-600">Mantente al dia con nuestras actividades</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {news.map(article => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </div>
              {news.length === 0 && (
                <p className="text-center text-gray-500 mt-8">No hay noticias por el momento.</p>
              )}
            </div>
          </section>
        )}

        {/* ===== ADMIN PANEL ===== */}
        {activeTab === 'admin' && isAdminMode && (
          <section className="py-12 px-4" data-testid="admin-panel">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-4xl font-bold text-gray-900">Panel de Administracion</h2>
                <Button variant="outline" onClick={() => { setIsAdminMode(false); setActiveTab('home'); }} data-testid="admin-logout-btn">
                  Cerrar Sesion
                </Button>
              </div>

              <Tabs defaultValue="properties" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="properties" data-testid="admin-tab-properties">Propiedades</TabsTrigger>
                  <TabsTrigger value="promotions" data-testid="admin-tab-promotions">Promociones</TabsTrigger>
                  <TabsTrigger value="news" data-testid="admin-tab-news">Noticias</TabsTrigger>
                  <TabsTrigger value="stories" data-testid="admin-tab-stories">Casos de Exito</TabsTrigger>
                  <TabsTrigger value="settings" data-testid="admin-tab-settings">Configuracion</TabsTrigger>
                </TabsList>

                {/* --- ADMIN: PROPERTIES --- */}
                <TabsContent value="properties" className="mt-8">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold">Gestionar Propiedades</h3>
                    <Dialog open={addPropertyOpen} onOpenChange={setAddPropertyOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-property-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Propiedad</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Crear Nueva Propiedad</DialogTitle></DialogHeader>
                        <PropertyForm onSave={saveProperty} onClose={() => setAddPropertyOpen(false)} />
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {properties.map(p => <PropertyCard key={p.id} property={p} isAdmin={true} />)}
                  </div>
                  {properties.length === 0 && <p className="text-center text-gray-500 mt-8">No hay propiedades. Crea una nueva.</p>}
                </TabsContent>

                {/* --- ADMIN: PROMOTIONS --- */}
                <TabsContent value="promotions" className="mt-8">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold">Gestionar Promociones</h3>
                    <Dialog open={addPromotionOpen} onOpenChange={setAddPromotionOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-promotion-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Promocion</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Crear Nueva Promocion</DialogTitle></DialogHeader>
                        <PromotionForm onSave={savePromotion} onClose={() => setAddPromotionOpen(false)} />
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-4">
                    {promotions.map(p => <AdminPromotionItem key={p.id} promotion={p} />)}
                  </div>
                  {promotions.length === 0 && <p className="text-center text-gray-500 mt-8">No hay promociones. Crea una nueva.</p>}
                </TabsContent>

                {/* --- ADMIN: NEWS --- */}
                <TabsContent value="news" className="mt-8">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold">Gestionar Noticias</h3>
                    <Dialog open={addNewsOpen} onOpenChange={setAddNewsOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-news-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Noticia</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Crear Nueva Noticia</DialogTitle></DialogHeader>
                        <NewsForm onSave={saveNews} onClose={() => setAddNewsOpen(false)} />
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-4">
                    {news.map(a => <AdminNewsItem key={a.id} article={a} />)}
                  </div>
                  {news.length === 0 && <p className="text-center text-gray-500 mt-8">No hay noticias. Crea una nueva.</p>}
                </TabsContent>

                {/* --- ADMIN: SUCCESS STORIES --- */}
                <TabsContent value="stories" className="mt-8">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold">Gestionar Casos de Exito</h3>
                    <Dialog open={addStoryOpen} onOpenChange={setAddStoryOpen}>
                      <DialogTrigger asChild>
                        <Button data-testid="add-story-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo Caso</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Crear Nuevo Caso de Exito</DialogTitle></DialogHeader>
                        <SuccessStoryForm onSave={saveSuccessStory} onClose={() => setAddStoryOpen(false)} />
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-4">
                    {successStories.map(s => <AdminStoryItem key={s.id} story={s} />)}
                  </div>
                  {successStories.length === 0 && <p className="text-center text-gray-500 mt-8">No hay casos de exito. Crea uno nuevo.</p>}
                </TabsContent>

                {/* --- ADMIN: SETTINGS --- */}
                <TabsContent value="settings" className="mt-8">
                  <Card>
                    <CardHeader><CardTitle>Configuracion del Sitio</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Nombre de la Empresa</Label>
                            <Input data-testid="settings-company-name" value={siteSettings.company_name || ''} onChange={(e) => setSiteSettings({...siteSettings, company_name: e.target.value})} />
                          </div>
                          <div>
                            <Label>Numero de WhatsApp</Label>
                            <Input data-testid="settings-whatsapp-number" value={siteSettings.whatsapp_number || ''} onChange={(e) => setSiteSettings({...siteSettings, whatsapp_number: e.target.value})} placeholder="+1234567890" />
                          </div>
                        </div>
                        <div>
                          <Label>Correo de Contacto</Label>
                          <Input data-testid="settings-contact-email" value={siteSettings.contact_email || ''} onChange={(e) => setSiteSettings({...siteSettings, contact_email: e.target.value})} placeholder="info@tuempresa.com" />
                        </div>
                        <div>
                          <Label>Descripcion de la Empresa</Label>
                          <Textarea data-testid="settings-company-desc" value={siteSettings.company_description || ''} onChange={(e) => setSiteSettings({...siteSettings, company_description: e.target.value})} />
                        </div>
                        <div>
                          <Label>Mensaje de WhatsApp (general)</Label>
                          <Textarea data-testid="settings-whatsapp-msg" value={siteSettings.whatsapp_message || ''} onChange={(e) => setSiteSettings({...siteSettings, whatsapp_message: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Color Primario</Label>
                            <Input type="color" value={siteSettings.primary_color || '#1f2937'} onChange={(e) => setSiteSettings({...siteSettings, primary_color: e.target.value})} />
                          </div>
                          <div>
                            <Label>Color Secundario</Label>
                            <Input type="color" value={siteSettings.secondary_color || '#f59e0b'} onChange={(e) => setSiteSettings({...siteSettings, secondary_color: e.target.value})} />
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-gray-700 mb-3">Redes Sociales</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label>Facebook (URL)</Label>
                              <Input data-testid="settings-facebook" value={siteSettings.facebook_url || ''} onChange={(e) => setSiteSettings({...siteSettings, facebook_url: e.target.value})} placeholder="https://facebook.com/tu-pagina" />
                            </div>
                            <div>
                              <Label>Instagram (URL)</Label>
                              <Input data-testid="settings-instagram" value={siteSettings.instagram_url || ''} onChange={(e) => setSiteSettings({...siteSettings, instagram_url: e.target.value})} placeholder="https://instagram.com/tu-cuenta" />
                            </div>
                            <div>
                              <Label>TikTok (URL)</Label>
                              <Input data-testid="settings-tiktok" value={siteSettings.tiktok_url || ''} onChange={(e) => setSiteSettings({...siteSettings, tiktok_url: e.target.value})} placeholder="https://tiktok.com/@tu-cuenta" />
                            </div>
                          </div>
                          <div className="mt-3">
                            <Label>WhatsApp (URL de canal, grupo o enlace directo)</Label>
                            <Input data-testid="settings-whatsapp-url" value={siteSettings.whatsapp_url || ''} onChange={(e) => setSiteSettings({...siteSettings, whatsapp_url: e.target.value})} placeholder="https://wa.me/tu-numero o enlace de canal" />
                          </div>
                        </div>

                        <Button data-testid="save-settings-btn" onClick={() => saveSettings(siteSettings)} className="w-full">Guardar Configuracion</Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Home className="w-6 h-6 text-amber-500 mr-2" />
                <h3 className="text-xl font-bold">{siteSettings.company_name || 'Barraza360'}</h3>
              </div>
              <p className="text-gray-400">{siteSettings.company_description || 'Empresa lider en bienes raices'}</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Venta de Propiedades</li>
                <li>Asesoria Inmobiliaria</li>
                <li>Gestion de Propiedades</li>
                <li>Inversiones</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center"><Phone className="w-4 h-4 mr-2" /><span>{siteSettings.whatsapp_number}</span></div>
                <div className="flex items-center"><Mail className="w-4 h-4 mr-2" /><span>{siteSettings.contact_email || 'info@barraza360.com'}</span></div>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Siguenos</h4>
              <div className="flex space-x-4">
                {siteSettings.facebook_url && (
                  <a href={siteSettings.facebook_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Facebook className="w-6 h-6" /></a>
                )}
                {siteSettings.instagram_url && (
                  <a href={siteSettings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors"><Instagram className="w-6 h-6" /></a>
                )}
                {siteSettings.tiktok_url && (
                  <a href={siteSettings.tiktok_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/></svg>
                  </a>
                )}
                {siteSettings.whatsapp_number && (
                  <button onClick={() => handleWhatsAppClick()} className="text-gray-400 hover:text-white transition-colors"><MessageCircle className="w-6 h-6" /></button>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 {siteSettings.company_name || 'Barraza360'}. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* View Property Detail */}
      {viewProperty && (
        <Dialog open={!!viewProperty} onOpenChange={() => setViewProperty(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="property-detail-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl">{viewProperty.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {viewProperty.image_url && (
                <img src={resolveImageUrl(viewProperty.image_url)} alt={viewProperty.title} className="w-full h-72 object-cover rounded-lg cursor-pointer" onClick={() => setSelectedImage(resolveImageUrl(viewProperty.image_url))} />
              )}
              {viewProperty.gallery_images && viewProperty.gallery_images.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {viewProperty.gallery_images.map((img, i) => (
                    <img key={i} src={resolveImageUrl(img)} alt={`Galeria ${i+1}`} className="w-full h-20 object-cover rounded cursor-pointer" onClick={() => setSelectedImage(resolveImageUrl(img))} />
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-gray-900">${viewProperty.price?.toLocaleString()}</div>
                <Badge className={viewProperty.status === 'available' ? 'bg-green-500' : viewProperty.status === 'sold' ? 'bg-red-500' : 'bg-blue-500'}>
                  {viewProperty.status === 'available' ? 'Disponible' : viewProperty.status === 'sold' ? 'Vendido' : 'Reservado'}
                </Badge>
              </div>
              {viewProperty.project_name && <p className="text-amber-600 font-medium">Proyecto: {viewProperty.project_name}</p>}
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{viewProperty.description}</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-500" /><span>{viewProperty.location}</span></div>
                <div className="flex items-center gap-2"><Square className="w-4 h-4 text-gray-500" /><span>{viewProperty.area}</span></div>
                {viewProperty.bedrooms && <div className="flex items-center gap-2"><Bed className="w-4 h-4 text-gray-500" /><span>{viewProperty.bedrooms} habitaciones</span></div>}
                {viewProperty.bathrooms && <div className="flex items-center gap-2"><Bath className="w-4 h-4 text-gray-500" /><span>{viewProperty.bathrooms} banos</span></div>}
                <div className="flex items-center gap-2">
                  {viewProperty.delivery_type === 'immediate'
                    ? <><Truck className="w-4 h-4 text-green-500" /><span className="text-green-600">Entrega Inmediata</span></>
                    : <><Clock className="w-4 h-4 text-blue-500" /><span className="text-blue-600">Entrega: {viewProperty.delivery_date}</span></>}
                </div>
              </div>
              {viewProperty.features && viewProperty.features.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Caracteristicas:</p>
                  <div className="flex flex-wrap gap-2">{viewProperty.features.map((f, i) => <Badge key={i} variant="secondary">{f}</Badge>)}</div>
                </div>
              )}
              <Button onClick={() => { setViewProperty(null); handleWhatsAppClick(viewProperty); }} className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> Consultar por WhatsApp
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* View Promotion Detail */}
      {viewPromotion && (
        <Dialog open={!!viewPromotion} onOpenChange={() => setViewPromotion(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="promotion-detail-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl">{viewPromotion.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {viewPromotion.image_url && (
                <img src={resolveImageUrl(viewPromotion.image_url)} alt={viewPromotion.title} className="w-full h-72 object-cover rounded-lg cursor-pointer" onClick={() => setSelectedImage(resolveImageUrl(viewPromotion.image_url))} />
              )}
              <p className="text-amber-600 font-medium">Proyecto: {viewPromotion.project_name}</p>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{viewPromotion.description}</p>
              {viewPromotion.benefits && viewPromotion.benefits.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Beneficios incluidos:</p>
                  <ul className="space-y-2">
                    {viewPromotion.benefits.map((b, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-700"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /> {b}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" /><span>Valido hasta: {viewPromotion.valid_until}</span>
              </div>
              {viewPromotion.terms_conditions && (
                <p className="text-sm text-gray-500 italic border-t pt-3">*{viewPromotion.terms_conditions}</p>
              )}
              <Button onClick={() => { setViewPromotion(null); handleWhatsAppClick(); }} className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> Consultar Promocion
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Property Dialog */}
      {selectedProperty && (
        <Dialog open={!!selectedProperty} onOpenChange={() => setSelectedProperty(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Editar Propiedad</DialogTitle></DialogHeader>
            <PropertyForm property={selectedProperty} onSave={saveProperty} onClose={() => setSelectedProperty(null)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Promotion Dialog */}
      {selectedPromotion && (
        <Dialog open={!!selectedPromotion} onOpenChange={() => setSelectedPromotion(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Editar Promocion</DialogTitle></DialogHeader>
            <PromotionForm promotion={selectedPromotion} onSave={savePromotion} onClose={() => setSelectedPromotion(null)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit News Dialog */}
      {selectedNews && (
        <Dialog open={!!selectedNews} onOpenChange={() => setSelectedNews(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Editar Noticia</DialogTitle></DialogHeader>
            <NewsForm article={selectedNews} onSave={saveNews} onClose={() => setSelectedNews(null)} />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Success Story Dialog */}
      {selectedStory && (
        <Dialog open={!!selectedStory} onOpenChange={() => setSelectedStory(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Editar Caso de Exito</DialogTitle></DialogHeader>
            <SuccessStoryForm story={selectedStory} onSave={saveSuccessStory} onClose={() => setSelectedStory(null)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default App;
